import { useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useStudioStore } from '@/store'
import { Card } from '@/components/ui'
import { LoadingScreen } from '@/components/LoadingScreen'
import { formatCurrency, formatNumber } from '@/lib/utils'
import { getChannelPresence } from '@/lib/analytics/subscriptionPresence'
import { useStudioPeriods } from '@/hooks'
import { useHistoryMetrics } from '@/hooks/useHistoryMetrics'
import { StatusLegend } from '@/components/ui/metrics'
import { exportHistoryToExcel } from '@/lib/excelExport'
import { HistoryToolbar } from '@/components/history/HistoryToolbar'
import {
  ClientsSection,
  MarketingSection,
  FunnelSection,
  SalesSection,
  RetentionSection,
  PrivateSessionsSection,
  TotalsSection,
} from '@/components/history/sections'

/**
 * Weekly-first implementation:
 * - Main source: studio_weekly_plan_vs_fact (extended with financial metrics)
 * - All metrics aggregated from weekly data
 * - No conversions or additional queries needed
 *
 * Migration: 20260217120000_extend_weekly_plan_vs_fact_with_finance.sql
 */

export function HistoryPage() {
  const { studioId, partnerNumber } = useParams<{ studioId: string; partnerNumber?: string }>()
  const navigate = useNavigate()
  const { studios } = useStudioStore()
  const studioIntId = parseInt(studioId || '0')
  const studioCurrency = useMemo(
    () => studios.find((s) => s.studio_internal_id === studioIntId)?.currency_code || 'RUB',
    [studios, studioIntId]
  )

  const {
    currentPeriod,
    availablePeriods,
    canGoPrev,
    canGoNext,
    goToPrevPeriod,
    goToNextPeriod,
    isCurrentPeriod,
    loading: periodsLoading,
  } = useStudioPeriods(studioIntId)

  const { metrics, loading: metricsLoading } = useHistoryMetrics(studioIntId, currentPeriod)
  const loading = periodsLoading || metricsLoading

  const fmtCurrency = (v: number) => formatCurrency(v, studioCurrency)
  const fmtNumber = (v: number) => formatNumber(v)
  const fmtRefund = (v: number) => fmtCurrency(v > 0 ? -v : v)
  const fmtConversion = (from: number, to: number): string => {
    if (from <= 0) return '—'
    const pct = (to / from) * 100
    return pct > 0 ? `${pct.toFixed(1)}%` : '—'
  }

  const weeksInPeriod = Math.max(Number(currentPeriod?.period_len_days) / 7, 1) || 1
  const weeklyAvg = (v: number) => v / weeksInPeriod

  const handleOpenPeriodPlan = () => {
    if (!currentPeriod) return
    const selectedMonth = `${currentPeriod.year_number}-${String(currentPeriod.month_number).padStart(2, '0')}`
    const targetPartnerNumber = partnerNumber || null
    if (!targetPartnerNumber) {
      navigate('/partners')
      return
    }

    navigate(`/partners/${targetPartnerNumber}/studios/${studioIntId}/studio-metrics-grid`, {
      state: { selectedMonth, useSelectedMonth: true },
    })
  }

  const getNextMonth = () => {
    if (!currentPeriod || availablePeriods.length === 0) return null
    const currentIndex = availablePeriods.findIndex((p) => p.period_id === currentPeriod.period_id)
    if (currentIndex === -1 || currentIndex >= availablePeriods.length - 1) {
      const { year_number: year, month_number: month } = currentPeriod
      const nextMonth = month === 12 ? 1 : month + 1
      const nextYear = month === 12 ? year + 1 : year
      return `${nextYear}-${String(nextMonth).padStart(2, '0')}`
    }
    const next = availablePeriods[currentIndex + 1]
    return `${next.year_number}-${String(next.month_number).padStart(2, '0')}`
  }

  const handleSetNextMonthPlan = () => {
    const nextMonth = getNextMonth()
    if (!nextMonth) return
    const targetPartnerNumber = partnerNumber || null
    if (!targetPartnerNumber) {
      navigate('/partners')
      return
    }

    navigate(`/partners/${targetPartnerNumber}/studios/${studioIntId}/studio-metrics-grid`, {
      state: { selectedMonth: nextMonth, useSelectedMonth: true },
    })
  }

  const showExcelExportButton = false
  const showCurrentMonthPlanButton = false

  const handleExcelExport = async () => {
    if (!currentPeriod) return
    try {
      await exportHistoryToExcel({
        studioId: studioIntId,
        periodStart: currentPeriod.period_start,
        periodEnd: currentPeriod.period_end,
        monthNumber: currentPeriod.month_number,
        yearNumber: currentPeriod.year_number,
      })
    } catch (error) {
      console.error('Ошибка при экспорте:', error)
    }
  }

  if (loading) return <LoadingScreen />

  if (!currentPeriod) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center p-8">
          <p className="text-muted-foreground">Загрузка периодов...</p>
        </Card>
      </div>
    )
  }

  if (!metrics) {
    return (
      <div className="space-y-3">
        <HistoryToolbar
          period={currentPeriod}
          canGoNext={canGoNext}
          onPrevious={goToPrevPeriod}
          onNext={goToNextPeriod}
          onOpenPeriodPlan={handleOpenPeriodPlan}
          showExcelExport={showExcelExportButton}
          showNextMonthPlan={showCurrentMonthPlanButton}
          isCurrentPeriod={isCurrentPeriod}
          onExcelExport={handleExcelExport}
          onNextMonthPlan={handleSetNextMonthPlan}
        />
        <div className="min-h-[60vh] bg-background flex items-center justify-center p-4">
          <Card className="text-center p-8">
            <p className="text-muted-foreground">Нет данных за выбранный период</p>
          </Card>
        </div>
      </div>
    )
  }

  const hasMetricData = (metric: { plan: number; fact: number } | { fact: number }) =>
    ('plan' in metric ? metric.plan > 0 : false) || metric.fact > 0

  const hasNewSalesCountData = hasMetricData(metrics.new_sales_count)
  const hasNewSalesAmountData = hasMetricData(metrics.new_sales_amount)
  const hasNewSalesAvgCheckData = hasNewSalesCountData || hasNewSalesAmountData
  const hasTrialPaymentsData = hasMetricData(metrics.trial_payments)
  const hasNewSubscriptionsCountData = hasMetricData(metrics.new_subscriptions_count)
  const hasNewSubscriptionsAmountData = hasMetricData(metrics.new_subscriptions_amount)
  const hasNewSubscriptionsAvgCheckData = hasNewSubscriptionsCountData || hasNewSubscriptionsAmountData
  const hasMembershipFeesNewData = hasMetricData(metrics.membership_fees_new)
  const hasRenewalsCountData = hasMetricData(metrics.renewals_count)
  const hasRenewalsAmountData = hasMetricData(metrics.renewals_amount)
  const hasRenewalsAvgCheckData = hasRenewalsCountData || hasRenewalsAmountData
  const hasUpgradesCountData = hasMetricData(metrics.upgrades_count)
  const hasUpgradesAmountData = hasMetricData(metrics.upgrades_amount)
  const hasUpgradesAvgCheckData = hasUpgradesCountData || hasUpgradesAmountData
  const hasReturningClientsCountData = hasMetricData(metrics.returning_clients_count)
  const hasReturningClientsAmountData = hasMetricData(metrics.returning_clients_amount)
  const hasReturningClientsAvgCheckData = hasReturningClientsCountData || hasReturningClientsAmountData
  const hasSubscriptionRenewalsCountData = hasMetricData(metrics.subscription_renewals_count)
  const hasSubscriptionRenewalsAmountData = hasMetricData(metrics.subscription_renewals_amount)
  const hasSubscriptionRenewalsAvgCheckData = hasSubscriptionRenewalsCountData || hasSubscriptionRenewalsAmountData
  const hasSubscriptionUpgradesCountData = hasMetricData(metrics.subscription_upgrades_count)
  const hasSubscriptionUpgradesAmountData = hasMetricData(metrics.subscription_upgrades_amount)
  const hasSubscriptionUpgradesAvgCheckData = hasSubscriptionUpgradesCountData || hasSubscriptionUpgradesAmountData
  const hasSubscriptionReturningClientsCountData = hasMetricData(metrics.subscription_returning_clients_count)
  const hasSubscriptionReturningClientsAmountData = hasMetricData(metrics.subscription_returning_clients_amount)
  const hasSubscriptionReturningClientsAvgCheckData =
    hasSubscriptionReturningClientsCountData || hasSubscriptionReturningClientsAmountData
  const hasMembershipFeesUpgradesData = hasMetricData(metrics.membership_fees_subscription_upgrades)
  const hasMembershipFeesReturningData = hasMetricData(metrics.membership_fees_subscription_returning)
  const hasPrivateSessionsAvgCheckData =
    hasMetricData(metrics.private_sessions_count) || hasMetricData(metrics.private_sessions_amount)

  const salesPresence = getChannelPresence(
    [hasNewSalesCountData ? 1 : 0, hasNewSalesAmountData ? 1 : 0, hasTrialPaymentsData ? 1 : 0],
    [hasNewSubscriptionsCountData ? 1 : 0, hasNewSubscriptionsAmountData ? 1 : 0, hasMembershipFeesNewData ? 1 : 0]
  )
  const hasRegularSalesData = salesPresence.hasRegular
  const hasSubscriptionSalesData = salesPresence.hasSubscription

  const retentionPresence = getChannelPresence(
    [
      hasRenewalsCountData ? 1 : 0,
      hasRenewalsAmountData ? 1 : 0,
      hasUpgradesCountData ? 1 : 0,
      hasUpgradesAmountData ? 1 : 0,
      hasReturningClientsCountData ? 1 : 0,
      hasReturningClientsAmountData ? 1 : 0,
    ],
    [
      hasSubscriptionRenewalsCountData ? 1 : 0,
      hasSubscriptionRenewalsAmountData ? 1 : 0,
      hasSubscriptionUpgradesCountData ? 1 : 0,
      hasSubscriptionUpgradesAmountData ? 1 : 0,
      hasSubscriptionReturningClientsCountData ? 1 : 0,
      hasSubscriptionReturningClientsAmountData ? 1 : 0,
      hasMembershipFeesUpgradesData ? 1 : 0,
      hasMembershipFeesReturningData ? 1 : 0,
    ]
  )
  const hasRegularRetentionData = retentionPresence.hasRegular
  const hasSubscriptionRetentionData = retentionPresence.hasSubscription

  return (
    <div className="space-y-3">
      <HistoryToolbar
        period={currentPeriod}
        canGoNext={canGoNext}
        onPrevious={goToPrevPeriod}
        onNext={goToNextPeriod}
        onOpenPeriodPlan={handleOpenPeriodPlan}
        showExcelExport={showExcelExportButton}
        showNextMonthPlan={showCurrentMonthPlanButton}
        isCurrentPeriod={isCurrentPeriod}
        onExcelExport={handleExcelExport}
        onNextMonthPlan={handleSetNextMonthPlan}
      />

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border sticky top-0">
              <tr>
                <th className="text-left p-3 text-sm font-medium">Показатель</th>
                <th className="text-right p-3 text-sm font-medium">План (ср в неделю)</th>
                <th className="text-right p-3 text-sm font-medium">Факт (ср в неделю)</th>
                <th className="text-right p-3 text-sm font-medium">План (месяц)</th>
                <th className="text-right p-3 text-sm font-medium">Факт (месяц)</th>
                <th className="text-center p-3 text-sm font-medium w-20">%</th>
              </tr>
            </thead>
            <tbody>
              <ClientsSection metrics={metrics} formatNumber={fmtNumber} />
              <MarketingSection metrics={metrics} weeklyAvg={weeklyAvg} formatCurrency={fmtCurrency} />
              <FunnelSection
                metrics={metrics}
                weeklyAvg={weeklyAvg}
                formatNumber={fmtNumber}
                formatConversion={fmtConversion}
              />
              <SalesSection
                metrics={metrics}
                weeklyAvg={weeklyAvg}
                formatNumber={fmtNumber}
                formatCurrency={fmtCurrency}
                formatConversion={fmtConversion}
                hasRegularSalesData={hasRegularSalesData}
                hasNewSalesCountData={hasNewSalesCountData}
                hasNewSalesAmountData={hasNewSalesAmountData}
                hasNewSalesAvgCheckData={hasNewSalesAvgCheckData}
                hasTrialPaymentsData={hasTrialPaymentsData}
                hasSubscriptionSalesData={hasSubscriptionSalesData}
                hasNewSubscriptionsCountData={hasNewSubscriptionsCountData}
                hasNewSubscriptionsAmountData={hasNewSubscriptionsAmountData}
                hasNewSubscriptionsAvgCheckData={hasNewSubscriptionsAvgCheckData}
                hasMembershipFeesNewData={hasMembershipFeesNewData}
              />
              <RetentionSection
                metrics={metrics}
                weeklyAvg={weeklyAvg}
                formatNumber={fmtNumber}
                formatCurrency={fmtCurrency}
                hasRegularRetentionData={hasRegularRetentionData}
                hasRenewalsCountData={hasRenewalsCountData}
                hasRenewalsAmountData={hasRenewalsAmountData}
                hasRenewalsAvgCheckData={hasRenewalsAvgCheckData}
                hasUpgradesCountData={hasUpgradesCountData}
                hasUpgradesAmountData={hasUpgradesAmountData}
                hasUpgradesAvgCheckData={hasUpgradesAvgCheckData}
                hasReturningClientsCountData={hasReturningClientsCountData}
                hasReturningClientsAmountData={hasReturningClientsAmountData}
                hasReturningClientsAvgCheckData={hasReturningClientsAvgCheckData}
                hasSubscriptionRetentionData={hasSubscriptionRetentionData}
                hasSubscriptionRenewalsCountData={hasSubscriptionRenewalsCountData}
                hasSubscriptionRenewalsAmountData={hasSubscriptionRenewalsAmountData}
                hasSubscriptionRenewalsAvgCheckData={hasSubscriptionRenewalsAvgCheckData}
                hasSubscriptionUpgradesCountData={hasSubscriptionUpgradesCountData}
                hasSubscriptionUpgradesAmountData={hasSubscriptionUpgradesAmountData}
                hasSubscriptionUpgradesAvgCheckData={hasSubscriptionUpgradesAvgCheckData}
                hasMembershipFeesUpgradesData={hasMembershipFeesUpgradesData}
                hasSubscriptionReturningClientsCountData={hasSubscriptionReturningClientsCountData}
                hasSubscriptionReturningClientsAmountData={hasSubscriptionReturningClientsAmountData}
                hasSubscriptionReturningClientsAvgCheckData={hasSubscriptionReturningClientsAvgCheckData}
                hasMembershipFeesReturningData={hasMembershipFeesReturningData}
              />
              <PrivateSessionsSection
                metrics={metrics}
                weeklyAvg={weeklyAvg}
                formatNumber={fmtNumber}
                formatCurrency={fmtCurrency}
                hasPrivateSessionsAvgCheckData={hasPrivateSessionsAvgCheckData}
              />
              <TotalsSection
                metrics={metrics}
                weeklyAvg={weeklyAvg}
                formatCurrency={fmtCurrency}
                formatRefund={fmtRefund}
              />
            </tbody>
          </table>
        </div>
      </Card>

      <StatusLegend />
    </div>
  )
}
